/**
 * Transforms API chart data format to Plotly.js format
 */

export interface ApiChartData {
  chartType: string;
  title: string;
  xAxis: {
    label: string;
    data: any[];
  };
  yAxis: {
    label: string;
    data: any[];
  };
}

export interface PlotlyData {
  data: any[];
  layout: any;
}

/**
 * Transforms structured chart data from API to Plotly format
 */
export const transformToPlotlyFormat = (structuredData: ApiChartData): PlotlyData => {
  if (!structuredData || !structuredData.xAxis || !structuredData.yAxis) {
    return { data: [], layout: {} };
  }

  const { chartType, title, xAxis, yAxis } = structuredData;

  // Base trace configuration
  const trace: any = {
    x: xAxis.data,
    y: yAxis.data,
    type: getPlotlyChartType(chartType),
    name: title || 'Data'
  };

  // Chart-specific styling
  switch (chartType.toLowerCase()) {
    case 'bar':
      trace.marker = {
        color: '#0078d4', // Microsoft blue
        opacity: 0.8
      };
      break;
    case 'line':
      trace.mode = 'lines+markers';
      trace.line = { color: '#0078d4', width: 2 };
      trace.marker = { color: '#0078d4', size: 6 };
      break;
    case 'pie':
      trace.labels = xAxis.data;
      trace.values = yAxis.data;
      trace.type = 'pie';
      trace.textinfo = 'label+percent';
      trace.textposition = 'outside';
      trace.marker = {
        colors: ['#0078d4', '#40e0d0', '#ee82ee', '#90ee90', '#ffa500', '#ff6347']
      };
      break;
    case 'scatter':
      trace.mode = 'markers';
      trace.marker = {
        color: '#0078d4',
        size: 8,
        opacity: 0.8
      };
      break;
    default:
      // Default to bar chart
      trace.type = 'bar';
      trace.marker = { color: '#0078d4', opacity: 0.8 };
  }

  // Layout configuration
  const layout: any = {
    title: {
      text: title || 'Chart',
      font: { size: 16, color: '#323130' }
    },
    autosize: true,
    responsive: true,
    margin: { l: 50, r: 50, t: 50, b: 50 },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    font: { family: 'Segoe UI, sans-serif', size: 12, color: '#323130' }
  };

  // Add axis labels for chart types that use them
  if (!['pie'].includes(chartType.toLowerCase())) {
    layout.xaxis = {
      title: xAxis.label || 'X Axis',
      titlefont: { size: 14, color: '#323130' },
      tickfont: { size: 12, color: '#605e5c' },
      gridcolor: '#edebe9',
      zerolinecolor: '#edebe9'
    };
    
    layout.yaxis = {
      title: yAxis.label || 'Y Axis',
      titlefont: { size: 14, color: '#323130' },
      tickfont: { size: 12, color: '#605e5c' },
      gridcolor: '#edebe9',
      zerolinecolor: '#edebe9'
    };
  }

  return {
    data: [trace],
    layout
  };
};

/**
 * Maps API chart types to Plotly chart types
 */
const getPlotlyChartType = (apiChartType: string): string => {
  const typeMap: Record<string, string> = {
    'bar': 'bar',
    'line': 'scatter',
    'pie': 'pie',
    'scatter': 'scatter',
    'histogram': 'histogram',
    'box': 'box'
  };

  return typeMap[apiChartType.toLowerCase()] || 'bar';
};

/**
 * Validates if data has the expected structure for chart rendering
 */
export const validateChartData = (structuredData: any): boolean => {
  return !!(
    structuredData &&
    structuredData.xAxis &&
    structuredData.yAxis &&
    Array.isArray(structuredData.xAxis.data) &&
    Array.isArray(structuredData.yAxis.data) &&
    structuredData.xAxis.data.length > 0 &&
    structuredData.yAxis.data.length > 0
  );
};